import React, { useState, useRef, useEffect } from 'react';
import { Dropdown } from 'react-bootstrap';

const ColumnHeaderDropdown = ({ field, onSort, onGroup, onSummarize, onMove, onRemove, isSummarized = false, aggregationType = null, isGrouped = false }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showSummarizeSubmenu, setShowSummarizeSubmenu] = useState(false);
  const [submenuPosition, setSubmenuPosition] = useState('right');
  const dropdownRef = useRef(null);
  const submenuRef = useRef(null);
  const submenuTimeoutRef = useRef(null);

  // Helper function to convert aggregation type to readable label
  const getAggregationLabel = (type) => {
    const labels = {
      'sum': 'Sum',
      'avg': 'Average',
      'min': 'Minimum',
      'max': 'Maximum',
      'count': 'Count'
    };
    return labels[type] || type;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
        setShowSummarizeSubmenu(false);
        if (submenuTimeoutRef.current) {
          clearTimeout(submenuTimeoutRef.current);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (submenuTimeoutRef.current) {
        clearTimeout(submenuTimeoutRef.current);
      }
    };
  }, []);

  const normalizedFieldType = (field.data_type || field.field_type || '').toLowerCase();
  const isNumericField = [
    'number',
    'integer',
    'decimal',
    'currency',
    'percent',
    'rollupsummary',
    'autonumber',
    'numeric',
    'float',
    'double',
    'real',
    'bigint',
    'smallint',
    'money'
  ].includes(normalizedFieldType);

  const handleSummarizeClick = (aggregation) => {
    // If the same aggregation is already selected, remove it
    if (isSummarized && aggregationType === aggregation) {
      onSummarize('remove_summarize', field, null);
    } else {
      onSummarize('summarize', field, aggregation);
    }
    setShowDropdown(false);
    setShowSummarizeSubmenu(false);
  };

  const handleSortClick = (direction) => {
    onSort('sort', field, direction);
    setShowDropdown(false);
  };

  const handleGroupClick = (type) => {
    onGroup('group', field, type);
    setShowDropdown(false);
  };

  const handleMoveClick = (direction) => {
    onMove('move', field, direction);
    setShowDropdown(false);
  };

  const handleRemoveClick = () => {
    onRemove('remove', field, null);
    setShowDropdown(false);
  };

  return (
    <div className="column-header-dropdown" ref={dropdownRef}>
      <div
        className="header-cell-content"
        onClick={() => setShowDropdown(!showDropdown)}
        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span>
            {field.field_label}
            {isSummarized && aggregationType && (
              <span style={{
                color: '#3b82f6',
                fontWeight: '600',
                marginLeft: '4px',
                fontSize: '0.9em'
              }}>
                ({getAggregationLabel(aggregationType)})
              </span>
            )}
          </span>
          {isGrouped && (
            <span className="grouped-indicator" title="Grouped by this field">
              ⊞
            </span>
          )}
          {isSummarized && (
            <span className="summarized-indicator" title={`Summarized as ${aggregationType}`}>
              Σ
            </span>
          )}
        </div>
        <span className="dropdown-arrow">▼</span>
      </div>

      {showDropdown && (
        <div className="header-dropdown-menu">
          <div className="dropdown-item" onClick={() => handleSortClick('asc')}>
            <span className="dropdown-icon">↑</span>
            Sort Ascending
          </div>
          <div className="dropdown-item" onClick={() => handleSortClick('desc')}>
            <span className="dropdown-icon">↓</span>
            Sort Descending
          </div>
          <div className={`dropdown-item ${isGrouped ? 'selected' : ''}`} onClick={() => handleGroupClick('rows')}>
            <span className="dropdown-icon">⊞</span>
            {isGrouped ? 'Remove Grouping' : 'Group Rows by This Field'}
          </div>
          <div className="dropdown-item" onClick={() => handleGroupClick('columns')}>
            <span className="dropdown-icon">⊟</span>
            Group Columns by This Field
          </div>

          {isNumericField && (
            <div
              className="dropdown-item has-submenu"
              onMouseEnter={(event) => {
                // Clear any pending timeout
                if (submenuTimeoutRef.current) {
                  clearTimeout(submenuTimeoutRef.current);
                  submenuTimeoutRef.current = null;
                }

                const menuRect = dropdownRef.current?.getBoundingClientRect();
                const submenuWidth = 180; // approximate width
                const viewportWidth = window.innerWidth;

                if (menuRect) {
                  const remainingSpace = viewportWidth - (menuRect.right + submenuWidth);
                  if (remainingSpace < 24) {
                    setSubmenuPosition('left');
                  } else {
                    setSubmenuPosition('right');
                  }
                } else {
                  setSubmenuPosition('right');
                }
                setShowSummarizeSubmenu(true);
              }}
              onMouseLeave={(event) => {
                const relatedTarget = event.relatedTarget;
                // Check if mouse is moving to submenu
                if (
                  submenuRef.current &&
                  relatedTarget &&
                  submenuRef.current.contains(relatedTarget)
                ) {
                  return;
                }
                // Add delay before closing to allow mouse to move to submenu
                submenuTimeoutRef.current = setTimeout(() => {
                  setShowSummarizeSubmenu(false);
                }, 200);
              }}
            >
              <span className="dropdown-icon">Σ</span>
              Summarize
              <span className="submenu-arrow">▶</span>

              {showSummarizeSubmenu && (
                <div
                  ref={submenuRef}
                  className={`submenu submenu-${submenuPosition}`}
                  onMouseLeave={(event) => {
                    const relatedTarget = event.relatedTarget;
                    if (
                      dropdownRef.current &&
                      relatedTarget &&
                      dropdownRef.current.contains(relatedTarget)
                    ) {
                      return;
                    }
                    // Add delay before closing
                    submenuTimeoutRef.current = setTimeout(() => {
                      setShowSummarizeSubmenu(false);
                    }, 200);
                  }}
                  onMouseEnter={() => {
                    // Clear any pending timeout when mouse enters submenu
                    if (submenuTimeoutRef.current) {
                      clearTimeout(submenuTimeoutRef.current);
                      submenuTimeoutRef.current = null;
                    }
                    setShowSummarizeSubmenu(true);
                  }}
                >
                  <div className="submenu-item" onClick={() => handleSummarizeClick('sum')}>
                    <span className="submenu-checkbox">
                      {isSummarized && aggregationType === 'sum' ? '✓' : ''}
                    </span>
                    Sum
                  </div>
                  <div className="submenu-item" onClick={() => handleSummarizeClick('avg')}>
                    <span className="submenu-checkbox">
                      {isSummarized && aggregationType === 'avg' ? '✓' : ''}
                    </span>
                    Average
                  </div>
                  <div className="submenu-item" onClick={() => handleSummarizeClick('max')}>
                    <span className="submenu-checkbox">
                      {isSummarized && aggregationType === 'max' ? '✓' : ''}
                    </span>
                    Max
                  </div>
                  <div className="submenu-item" onClick={() => handleSummarizeClick('min')}>
                    <span className="submenu-checkbox">
                      {isSummarized && aggregationType === 'min' ? '✓' : ''}
                    </span>
                    Min
                  </div>
                  <div className="submenu-item" onClick={() => handleSummarizeClick('count')}>
                    <span className="submenu-checkbox">
                      {isSummarized && aggregationType === 'count' ? '✓' : ''}
                    </span>
                    Count
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="dropdown-item" onClick={() => handleMoveClick('left')}>
            <span className="dropdown-icon">←</span>
            Move Left
          </div>
          <div className="dropdown-item" onClick={() => handleMoveClick('right')}>
            <span className="dropdown-icon">→</span>
            Move Right
          </div>
          <div className="dropdown-item danger" onClick={handleRemoveClick}>
            <span className="dropdown-icon">×</span>
            Remove Column
          </div>
        </div>
      )}
    </div>
  );
};

export default ColumnHeaderDropdown;
